import { ArrowClockwiseIcon, CalendarBlankIcon } from '@phosphor-icons/react';

import { TextButton } from '@/components/common/text-button';
import { Drawer } from '@/components/feedback/drawer';
import type { OrderDetail } from '@/features/orders';
import { useOrderReviews } from '@/features/orders';

import { formatReviewDate } from '../format';

import { DrawerSkeleton } from './drawer-skeleton';
import styles from './reviews-drawer.module.scss';
import { StarRating } from './star-rating';

type Props = { order: OrderDetail; open: boolean; onClose: () => void };

/** Spec's decided copy for a failed reviews fetch (mirrors WarrantyClaimsDrawer's own LOAD_ERROR). */
const LOAD_ERROR = "We couldn't load your reviews. Please try again.";

function ReviewsBody({ id }: { id: string }) {
  const { data: review, isPending, isError, refetch } = useOrderReviews(id, true);

  if (isPending) return <DrawerSkeleton label="Loading reviews" />;

  if (isError) {
    return (
      <div className={styles.notice}>
        <p className={styles.noticeText}>{LOAD_ERROR}</p>
        <TextButton
          tone="brand"
          size="sm"
          startIcon={<ArrowClockwiseIcon size={16} />}
          onClick={() => void refetch()}
        >
          Try again
        </TextButton>
      </div>
    );
  }

  if (!review) return null;

  // Order-level written review (ticket 02) — rendered once, beneath the device rows, keyed off
  // `comment !== null` (never a placeholder for a null comment). The date line reads the *updated*
  // date labelled "Updated On" once the Customer has amended it, else the *submitted* date labelled
  // "Reviewed On" — `commentIsEdited` is the backend's own resolved boolean, never a client-side
  // timestamp comparison.
  const reviewedAt = review.commentIsEdited ? review.commentUpdatedAt : review.commentSubmittedAt;

  return (
    <>
      <ul className={styles.list}>
        {review.items.map((item) => (
          <li key={item.orderItemId} className={styles.row}>
            {item.imageUrl ? (
              <img className={styles.thumb} src={item.imageUrl} alt="" />
            ) : (
              <span className={styles.thumb} aria-hidden />
            )}
            <div className={styles.details}>
              <div className={styles.nameAndDate}>
                <span className={styles.name}>{item.productName}</span>
                <span className={styles.date}>
                  {item.isRated && item.ratedAt ? formatReviewDate(item.ratedAt) : 'Not yet rated'}
                </span>
              </div>
              <StarRating value={item.rating ?? 0} isRated={item.isRated} />
            </div>
          </li>
        ))}
      </ul>
      {review.comment !== null ? (
        <div className={styles.review}>
          {reviewedAt ? (
            <p className={styles.reviewDate}>
              <CalendarBlankIcon size={20} aria-hidden />
              {(review.commentIsEdited ? 'Updated On ' : 'Reviewed On ') +
                formatReviewDate(reviewedAt)}
            </p>
          ) : null}
          <div className={styles.reviewBox}>
            {review.comment.split('\n\n').map((paragraph, index) => (
              <p key={index} className={styles.reviewParagraph}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

export function ReviewsDrawer({ order, open, onClose }: Props) {
  return (
    <Drawer open={open} onClose={onClose} title="Review(s)" hideFooter roundedBody>
      {open ? <ReviewsBody id={order.id} /> : null}
    </Drawer>
  );
}
